import React, { useState, useRef } from 'react';
import { X, MessageSquare, Bug, Lightbulb, HelpCircle, Upload, Image as ImageIcon } from 'lucide-react';
import { submitFeedback } from '../../services/apiService.ts';
import { supabase } from '../../services/supabaseClient.ts';
import type { FeedbackModalProps, FeedbackType, FeedbackFormData } from '../../types/feedback.ts';

export default function FeedbackModal({ isOpen, onClose, initialPage }: FeedbackModalProps) {
  const [formData, setFormData] = useState<FeedbackFormData>({
    type: 'bug',
    message: '',
    screenshot: null,
  });
  const [page, setPage] = useState(initialPage || window.location.pathname);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const messageLength = formData.message.length;
  const maxLength = 500;
  const isFormValid = formData.message.trim().length > 0 && messageLength <= maxLength;

  const feedbackTypes: { value: FeedbackType; label: string; icon: React.ReactNode; description: string }[] = [
    {
      value: 'bug',
      label: 'Bug Report',
      icon: <Bug className="w-5 h-5" />,
      description: 'Something is broken or not working as expected',
    },
    {
      value: 'feature',
      label: 'Feature Request',
      icon: <Lightbulb className="w-5 h-5" />,
      description: 'Suggest a new feature or improvement',
    },
    {
      value: 'other',
      label: 'Other',
      icon: <HelpCircle className="w-5 h-5" />,
      description: 'General feedback or questions',
    },
  ];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB');
      return;
    }

    setFormData(prev => ({ ...prev, screenshot: file }));
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
    
    setError('');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInputRef.current.files = dataTransfer.files;
        handleFileSelect({ target: { files: dataTransfer.files } } as any);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, screenshot: null }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadScreenshot = async (file: File): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `feedback/${user.id}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('uploads')
        .upload(filePath, file);

      if (error) {
        console.error('[FeedbackModal] Screenshot upload error:', error);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('uploads')
        .getPublicUrl(data.path);

      return publicUrl;
    } catch (error) {
      console.error('[FeedbackModal] Screenshot upload failed:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[FeedbackModal] Submitting feedback');

    if (!isFormValid) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let screenshotUrl: string | undefined;

      // Upload screenshot if provided
      if (formData.screenshot) {
        console.log('[FeedbackModal] Uploading screenshot...');
        screenshotUrl = await uploadScreenshot(formData.screenshot) || undefined;
        if (!screenshotUrl && formData.screenshot) {
          console.warn('[FeedbackModal] Screenshot upload failed, proceeding without it');
        }
      }

      // Submit feedback
      const request = {
        page: page.trim(),
        type: formData.type,
        message: formData.message.trim(),
        screenshot: screenshotUrl,
      };

      console.log('[FeedbackModal] Submitting feedback request:', request);
      const response = await submitFeedback(request);
      console.log('[FeedbackModal] Feedback submitted successfully:', response);

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err: any) {
      console.error('[FeedbackModal] Submission failed:', err);
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ type: 'bug', message: '', screenshot: null });
    setPage(initialPage || window.location.pathname);
    setError('');
    setSuccess(false);
    setLoading(false);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-black flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-black" />
            Send Feedback
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5 text-black" />
          </button>
        </div>

        {success ? (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-2">Thanks for your feedback!</h3>
            <p className="text-black">
              We'll review your feedback shortly and get back to you if needed.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Page field */}
            <div>
              <label htmlFor="page" className="block text-sm font-medium text-black mb-1">
                Page
              </label>
              <input
                id="page"
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-black placeholder:text-gray-500"
                value={page}
                onChange={(e) => setPage(e.target.value)}
                placeholder="/dashboard"
                disabled={loading}
                style={{ color: 'black !important' }}
              />
            </div>

            {/* Feedback type */}
            <div>
              <label className="block text-sm font-medium text-black mb-3">
                What type of feedback is this?
              </label>
              <div className="space-y-2">
                {feedbackTypes.map((type) => (
                  <label key={type.value} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value={type.value}
                      checked={formData.type === type.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as FeedbackType }))}
                      className="mt-1"
                      disabled={loading}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {type.icon}
                        <span className="font-medium text-black">{type.label}</span>
                      </div>
                      <p className="text-sm text-black">{type.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-black mb-1">
                Message *
              </label>
              <textarea
                id="message"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-black placeholder:text-gray-500"
                placeholder="Please describe your feedback in detail..."
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                disabled={loading}
                style={{ color: 'black !important' }}
              />
              <div className="flex justify-between items-center mt-1">
                <div className="text-xs text-black">
                  {messageLength > maxLength ? (
                    <span className="text-red-500">
                      {messageLength - maxLength} characters over limit
                    </span>
                  ) : (
                    <span className="text-black">
                      {messageLength}/{maxLength} characters
                    </span>
                  )}
                </div>
                <span className="text-xs text-black">* Required</span>
              </div>
            </div>

            {/* Screenshot upload */}
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Screenshot (optional)
              </label>
              
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Screenshot preview"
                    className="w-full h-32 object-cover border rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    disabled={loading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-black mb-2">
                    Drag and drop an image here, or click to select
                  </p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                    disabled={loading}
                  >
                    <Upload className="w-4 h-4" />
                    Choose file
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={loading}
                  />
                  <p className="text-xs text-black mt-1">PNG, JPG up to 5MB</p>
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit button */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-2 px-4 border border-gray-300 text-black font-semibold rounded-md hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid || loading}
                className="flex-1 py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
} 